import { motion } from "framer-motion";
import { Bike, Timer, Wrench, MessageCircle, CheckCircle2 } from "lucide-react";
import { waLink, WA_DEFAULT_MSG } from "@/lib/site";

const TIERS = [
  {
    id: "callout",
    icon: Bike,
    name: "Diritto di chiamata + diagnosi in scooter",
    price: "29€",
    note: "Gratis per gli abbonati",
    features: [
      "Uscita in scooter entro 90 minuti",
      "Diagnosi sul posto inclusa",
      "Gratis con convenzione B&B / Condominio",
    ],
    highlight: false,
  },
  {
    id: "micro",
    icon: Timer,
    name: "Micro-Intervento",
    price: "25€",
    note: "Fino a 30 minuti di lavoro",
    features: [
      "Galleggiante WC, flessibile, cinghia tapparella",
      "Presa o interruttore sostituito",
      "Ideale per i guasti lampo",
    ],
    highlight: false,
  },
  {
    id: "standard",
    icon: Wrench,
    name: "Intervento Standard",
    price: "45€",
    note: "Fino a 60 minuti di lavoro",
    features: [
      "Sifoni, piccole perdite, intasamenti",
      "Magnetotermico, lampadari, cilindri",
      "La fascia più richiesta a Napoli",
    ],
    highlight: true,
  },
];

export default function Tariffe() {
  return (
    <section id="tariffe" className="bg-[#060B19] py-16 sm:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.22em] text-[#FFB800]">
            02 — Tariffe trasparenti
          </p>
          <h2 className="font-display mt-3 text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Prezzi chiari. <span className="text-[#FF5500]">Zero sorprese.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            Sai quanto spendi prima ancora che lo scooter parta.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`pricing-card-${t.id}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 sm:p-8 ${
                t.highlight
                  ? "border-[#FF5500]/60 bg-[#0F1E3D] shadow-[0_0_50px_rgba(255,85,0,0.15)]"
                  : "border-white/10 bg-[#0D1833] hover:border-[#FF5500]/45"
              }`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-6 rounded-full bg-[#FF5500] px-3 py-1 font-mono-accent text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                  Più richiesto
                </span>
              )}
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#FFB800]/15">
                <t.icon className="h-6 w-6 text-[#FFB800]" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-xl font-bold uppercase tracking-tight">{t.name}</h3>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl font-black text-white">{t.price}</span>
              </div>
              <p className={`mt-1 font-mono-accent text-xs font-bold uppercase tracking-[0.16em] ${t.id === "callout" ? "text-[#25D366]" : "text-[#FFB800]"}`}>
                {t.note}
              </p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#25D366]" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 flex flex-col items-center justify-between gap-5 rounded-2xl border border-[#FFB800]/30 bg-[#FFB800]/5 p-6 sm:flex-row sm:p-7"
          data-testid="pricing-note-banner"
        >
          <p className="text-center text-sm font-semibold text-slate-200 sm:text-left sm:text-base">
            Preventivo confermato prima di iniziare e pagamento digitale a lavoro finito.
            <span className="mt-1 block text-xs font-normal text-slate-400 sm:text-sm">
              I ricambi sono sempre preventivati a parte, prima dell'intervento. Mai supplementi a sorpresa.
            </span>
          </p>
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-whatsapp-cta"
            className="flex min-h-[52px] shrink-0 items-center gap-2 rounded-xl bg-[#FF5500] px-7 font-bold text-white transition-all duration-200 hover:scale-[1.03] hover:bg-[#ff6622] active:scale-95"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
            Blocca il tuo intervento
          </a>
        </motion.div>
      </div>
    </section>
  );
}
